import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import { IMedia } from '@propertypal/shared/src/types/strapi';
import useTouchDevice from '@propertypal/web-app/src/hooks/useTouchDevice';
import React, {
  CSSProperties,
  FunctionComponent,
  KeyboardEventHandler,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTheme } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import 'swiper/css';
import { ArrowButton, Container, Curtain, ImageBox, NoImageBackground } from '../property/PropertySlider.style';
import { Text } from '../typography';
import { CarouselCaption, CarouselContainer, CloseButton } from './GalleryCarousel.style';

type GalleryItem = IMedia['attributes'] & { index: number };

interface Props {
  alt: string;
  images: GalleryItem[];
  imageIndex: number;
  setImageIndex: (index: number) => void;
  onClose: () => void;
  children?: ReactNode;
}

const MAX_RATIO = 0.66;

const getImageWidth = (
  width: number,
  height: number,
  windowWidth: number | undefined,
  sliderHeight: number,
): number => {
  const ratio = height / width;
  const imageWidth = sliderHeight / ratio;

  if (ratio < MAX_RATIO) {
    return Math.round(Math.min(sliderHeight / MAX_RATIO, windowWidth || 0));
  }

  return Math.round(Math.min(imageWidth, windowWidth || 0));
};

export const calculateHeight = (
  images: GalleryItem[],
  setHeight: (windowWidth: number, windowHeight: number, imageHeight: number) => number,
  windowWidth?: number,
  windowHeight?: number,
) => {
  if (windowWidth === undefined || windowHeight === undefined) {
    return 200;
  }

  let height: number = 0;

  images?.forEach((image) => {
    const ratio = (image.height || 0) / (image.width || 0);
    const imageHeight = ratio * windowWidth;

    if (height === 0 || (imageHeight < height && ratio > MAX_RATIO)) height = imageHeight;
  });

  return setHeight(windowWidth, windowHeight, height);
};

export const getActiveIndexes = (currentIndex: number, totalIndexes: number) => {
  if (totalIndexes <= 5) {
    return [0, 1, 2, 3, 4];
  }

  const indexes = [];

  for (let i = currentIndex - 2; i <= currentIndex + 2; i += 1) {
    if (i < 0) {
      indexes.push(totalIndexes + i);
    } else if (i >= totalIndexes) {
      indexes.push(i - totalIndexes);
    } else {
      indexes.push(i);
    }
  }

  return indexes;
};

const GalleryCarousel: FunctionComponent<Props> = (props) => {
  const windowSize = useWindowSize();
  const theme = useTheme();
  const touchDevice = useTouchDevice();
  const lastKeyPress = useRef<number>(0);
  const [swiper, setSwiper] = useState<SwiperClass>();
  const height = calculateHeight(
    props.images,
    (_, windowHeight) => windowHeight - 120,
    windowSize.width,
    windowSize.height,
  );
  const activeIndexes = getActiveIndexes(props.imageIndex || 0, props.images.length);

  const incrementIndex = () => {
    swiper?.slideNext();
  };

  const decrementIndex = () => {
    swiper?.slidePrev();
  };

  const handleImageClick = (image: GalleryItem) => {
    if (swiper) {
      const imageIndex = swiper.realIndex;

      if (
        (image.index < imageIndex || (imageIndex === 0 && image.index === props.images.length - 1)) &&
        !(imageIndex === props.images.length - 1 && image.index === 0)
      ) {
        decrementIndex();
      } else {
        incrementIndex();
      }
    }
  };

  const handleKeyPress: KeyboardEventHandler = (event) => {
    const now = new Date().getTime();

    if (document.scrollingElement && now > lastKeyPress.current + 500) {
      if (event.key === 'ArrowRight') incrementIndex();
      else if (event.key === 'ArrowLeft') decrementIndex();

      lastKeyPress.current = now;
    }
  };

  const handleKeyUp = () => {
    lastKeyPress.current = 0;
  };

  useEventListener('keydown', handleKeyPress);
  useEventListener('keyup', handleKeyUp);

  const caption = useMemo(() => {
    if (props.imageIndex != null) {
      const activeImage = props.images?.find((_, index) => props.imageIndex === index);
      if (activeImage) return activeImage?.caption || activeImage?.alternativeText || null;
    }
    return null;
  }, [JSON.stringify(props.images), props.imageIndex]);

  const closeGallery = (
    <CloseButton
      aria-label="Close property image modal"
      data-testid="closeImageModal"
      onClick={props.onClose}
      type="button"
    >
      <FontAwesomeIcon icon={faTimes} color={theme.white} size={30} />
    </CloseButton>
  );

  if (!windowSize.width || props.imageIndex === null) {
    return null;
  }

  if (props.images.length === 1) {
    const image = props.images[0];
    const width = getImageWidth(image.width || 0, image.height || 0, windowSize.width, height);
    return (
      <CarouselContainer
        role="region"
        aria-label="Article Gallery Images"
        tabIndex={0}
        data-testid="single-gallery-image"
      >
        {closeGallery}
        <Container style={{ height }} title={image.caption || image.alternativeText || props.alt || undefined}>
          {props.children}

          <img
            id="property-slider-image-1"
            src={image.url}
            style={{ width }}
            alt={image.alternativeText || image.caption || props.alt}
          />
        </Container>
        {caption && <CarouselCaption>{caption}</CarouselCaption>}
      </CarouselContainer>
    );
  }

  if (props.images.length === 0) {
    return (
      <CarouselContainer role="region" aria-label="Article Gallery Images" tabIndex={0} data-testid="no-image">
        {closeGallery}
        <Container role="region" aria-label="property images" tabIndex={0} style={{ height: '60vh' }}>
          <NoImageBackground>
            {props.children}

            <Text>No photo available</Text>
          </NoImageBackground>
        </Container>
      </CarouselContainer>
    );
  }

  return (
    <CarouselContainer role="region" aria-label="Article Gallery Images" tabIndex={0} data-testid="gallery-carousel">
      {closeGallery}
      <Container style={{ height }}>
        {/* Blocks ability on iOS to swipe history back and swipe the slider at the same time */}
        {/* Doing both at the same time creates a race condition between history back and history replace */}
        <Curtain style={{ left: 0 }} />
        <Curtain style={{ right: 0 }} />

        <Swiper
          initialSlide={props.imageIndex || 0}
          onSlideChange={(mSwiper) => {
            props.setImageIndex(mSwiper.realIndex);
          }}
          centeredSlides
          onSwiper={setSwiper}
          simulateTouch
          slidesPerView="auto"
          spaceBetween={10}
          maxBackfaceHiddenSlides={5}
          loop
          style={{ height: '100%' }}
          allowTouchMove={touchDevice}
        >
          {props.children}

          <ArrowButton
            aria-label="Previous image"
            side="left"
            onClick={decrementIndex}
            data-testid="slider-left"
            sitemapTouch={touchDevice}
          >
            <FontAwesomeIcon icon={faAngleLeft} color={theme.white} size={60} />
          </ArrowButton>

          <ArrowButton
            aria-label="Next image"
            side="right"
            onClick={incrementIndex}
            data-testid="slider-right"
            sitemapTouch={touchDevice}
          >
            <FontAwesomeIcon icon={faAngleRight} color={theme.white} size={60} />
          </ArrowButton>

          {props.images.map((image, index) => {
            const imageWidth = getImageWidth(image.width || 0, image.height || 0, windowSize.width, height);
            const imageHeight = 'auto';

            const style: CSSProperties = {
              width: imageWidth,
              display: 'block',
              backgroundColor: theme.backgroundLightest,
              height: imageHeight,
            };

            return (
              <SwiperSlide key={image.url} style={{ width: imageWidth }}>
                <ImageBox
                  data-testid={`slide-${index + 1}`}
                  style={{ height: imageHeight }}
                  onClick={() => handleImageClick(image)}
                  title={image.caption || image.alternativeText || props.alt || undefined}
                  $mode="single"
                >
                  {activeIndexes.includes(index) && (
                    <img
                      id={`article-slider-image-${index}`}
                      src={image.url}
                      alt={`${image.alternativeText || image.caption || props.alt} ${index + 1} of ${props.images
                        ?.length}`}
                      style={style}
                    />
                  )}
                </ImageBox>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Container>
      {caption && <CarouselCaption>{caption}</CarouselCaption>}
    </CarouselContainer>
  );
};

export default GalleryCarousel;
